import type { ReactNode } from 'react';

import { useHasLogtoRole, type PrivilegedRole } from '@/hooks/useHasLogtoRole';
import { RoleRequired } from './RoleRequired';

interface RoleGateProps {
  role: PrivilegedRole;
  children: ReactNode;
  /**
   * Cosa rendere quando il ruolo manca. Per difetto un pannello onesto
   * (`<RoleRequired />`), **non `null`** — v. la nota sotto.
   */
  fallback?: ReactNode;
}

/**
 * Renders `children` only when the signed-in user carries the given Logto
 * role. Optional `fallback` for users without it (default: render nothing).
 *
 * For route-level gating:
 * ```tsx
 * { path: 'shiny-thing', element: <RoleGate role="beta"><ShinyThing/></RoleGate> }
 * ```
 *
 * For inline conditional rendering (single line, no fallback needed) prefer
 * `useHasLogtoRole` directly:
 * ```tsx
 * const isBeta = useHasLogtoRole('beta');
 * return <>{...usual} {isBeta && <BetaPill/>}</>;
 * ```
 *
 * Distinct from <AdminGate>: AdminGate is a BUILD-level gate that runs once
 * in AppShell when `env.adminGateEnabled === true`. RoleGate is a FEATURE-
 * level gate that runs on any build, anywhere in the tree.
 *
 * 🔴 **IL DIFETTO PER CUI IL FALLBACK NON E' PIU' `null`** [M or45, 2026-09-06 su
 * `stg-tma`]: nessuno dei 13 usi passava un `fallback`, quindi «non hai il ruolo» si
 * rendeva come **niente** — `main` con `innerText` vuoto, indistinguibile da un guasto.
 * Ora il difetto e' un pannello che dice cosa serve e offre «Ricontrolla».
 * ⚠️ La SEMANTICA non cambia: chi non ha il ruolo continua a NON vedere la sezione. Cambia
 * solo cosa vede al suo posto. E chi passa un `fallback` esplicito non e' toccato.
 *
 * ⚠️ E il gate ora **rivaluta**: `useHasLogtoRole` rilegge i ruoli quando il token cambia
 * (segnale `qlash:tokens-changed` da `persistTokens`, piu' `visibilitychange`/`focus`). E'
 * simmetrico — un ruolo revocato fa sparire la sezione durante la sessione invece che al
 * caricamento dopo. Corretto: un gate che ricordasse un privilegio revocato sarebbe peggio.
 */
export function RoleGate({ role, children, fallback }: RoleGateProps) {
  const has = useHasLogtoRole(role);
  if (has) return <>{children}</>;
  // ⚠️ `fallback === undefined` (non passato) ⇒ il pannello. Un `fallback={null}` ESPLICITO
  // resta il vuoto, perche' chi lo scrive lo sta chiedendo: la differenza fra «non ho deciso»
  // e «ho deciso di non mostrare niente» e' proprio quella che il vecchio default cancellava.
  return <>{fallback === undefined ? <RoleRequired role={role} /> : fallback}</>;
}
